import { useState, useEffect } from 'react'

/**
 * 自定义 hook 用于管理嵌套对象中的特定字段
 * @param rootKey sessionStorage 的根键名
 * @param fieldKey 字段键名
 * @param initialValue 初始值
 * @returns [value, setValue] 返回当前值和设置值的函数
 */
export function useSessionStorageField<T>(rootKey: string, fieldKey: string, initialValue: T) {
  // 直接从 sessionStorage 读取数据，避免 useSessionStorage 的覆盖问题
  const getStoredValue = (): Record<string, any> => {
    try {
      const item = sessionStorage.getItem(rootKey)
      return item ? JSON.parse(item) : {}
    } catch (error) {
      console.warn(`Error reading sessionStorage key "${rootKey}":`, error)
      return {}
    }
  }

  const [rootData, setRootData] = useState<Record<string, any>>(getStoredValue)

  const value = rootData[fieldKey] !== undefined && rootData[fieldKey] !== null ? rootData[fieldKey] : initialValue

  // 如果字段不存在或为 null，将默认值写入 sessionStorage
  useEffect(() => {
    if (rootData[fieldKey] === undefined || rootData[fieldKey] === null) {
      const currentData = getStoredValue()
      const updatedData = {
        ...currentData,
        [fieldKey]: initialValue
      }
      setRootData(updatedData)
      try {
        sessionStorage.setItem(rootKey, JSON.stringify(updatedData))
      } catch (error) {
        console.warn(`Error setting initial value for sessionStorage key "${rootKey}":`, error)
      }
    }
  }, [rootKey, fieldKey, initialValue])

  const setValue = (newValue: T | ((val: T) => T)) => {
    const valueToStore = newValue instanceof Function ? newValue(value) : newValue

    // 先获取当前 sessionStorage 中的完整数据
    const currentData = getStoredValue()

    // 只更新指定字段，保留其他所有字段
    const updatedData = {
      ...currentData,
      [fieldKey]: valueToStore
    }

    // 更新状态
    setRootData(updatedData)

    // 更新 sessionStorage
    try {
      sessionStorage.setItem(rootKey, JSON.stringify(updatedData))

      // 触发自定义事件，通知其他组件数据已更新
      window.dispatchEvent(
        new CustomEvent('sessionStorageUpdate', {
          detail: { key: rootKey, data: updatedData }
        })
      )
    } catch (error) {
      console.warn(`Error setting sessionStorage key "${rootKey}":`, error)
    }
  }

  // 组件挂载时从 sessionStorage 读取最新值
  useEffect(() => {
    const item = sessionStorage.getItem(rootKey)
    if (item) {
      try {
        const parsedItem = JSON.parse(item)
        setRootData(parsedItem)
      } catch (error) {
        console.warn(`Error parsing sessionStorage key "${rootKey}":`, error)
      }
    }
  }, [rootKey])

  // 监听自定义事件，当其他组件更新数据时同步状态
  useEffect(() => {
    const handleStorageUpdate = (event: CustomEvent) => {
      if (event.detail.key === rootKey) {
        setRootData(event.detail.data)
      }
    }

    window.addEventListener('sessionStorageUpdate', handleStorageUpdate as EventListener)

    return () => {
      window.removeEventListener('sessionStorageUpdate', handleStorageUpdate as EventListener)
    }
  }, [rootKey])

  return [value, setValue] as const
}
